import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { Alert, Spinner } from '@/components/ui/misc';
import { GoogleIcon } from '@/components/GoogleIcon';
import { AuthShell } from './Login';
import { useAuth } from '@/context/AuthContext';

export function Register() {
  const { registerEmail, signInGoogle, configured } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setBusy(true);
    try {
      await registerEmail(email, password, name);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(friendly(err));
    } finally {
      setBusy(false);
    }
  }

  async function google() {
    setError(null);
    setBusy(true);
    try {
      await signInGoogle();
      navigate('/app', { replace: true });
    } catch (err) {
      setError(friendly(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Create your account" subtitle="Save reports and track visibility over time">
      {!configured && (
        <Alert tone="error">Authentication is not configured yet. Set the Firebase web keys.</Alert>
      )}
      <Button variant="secondary" className="w-full" onClick={google} disabled={busy || !configured}>
        <GoogleIcon /> Continue with Google
      </Button>
      <div className="my-4 flex items-center gap-3 text-xs text-text-muted">
        <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            minLength={6}
          />
          <p className="mt-1 text-xs text-text-muted">At least 6 characters.</p>
        </div>
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" className="w-full" disabled={busy || !configured}>
          {busy ? <Spinner /> : 'Create account'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-text-secondary">
        Already have an account?{' '}
        <Link to="/login" className="link-accent font-medium">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}

function friendly(err: unknown): string {
  const code = (err as { code?: string })?.code ?? '';
  if (code.includes('email-already-in-use')) return 'An account with that email already exists.';
  if (code.includes('invalid-email')) return 'Please enter a valid email address.';
  if (code.includes('weak-password')) return 'Please choose a stronger password.';
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}
